import { useSymbolIcon } from "@/shared/lib/hooks/useSymbolIcon";
import { accountType } from "@/shared/api/browser/accountType";
import { bonusStore } from "@/shared/stores/BonusStore";
import { tickerStore } from "@/shared/stores/TickerStore";
import { userStore } from "@/shared/stores/UserStore";
import { formatPriceValue } from "@/shared/ui/TradingChart/utils/chart-utils";
import { getPairMaxPayoutPercent } from "@/types/index";
import type { AssetType } from "@cronika/proto/common/trading_pb";
import { Cross, Down, StarOutline } from "@cronika/assets/icons";
import { Button, cn } from "@cronika/ui";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useState } from "react";
import { usePairs } from "./hooks/usePairs";
import { AddPair } from "./modules/add-pair";
import { useMediaQuery } from "usehooks-ts";

const SymbolIcon: FC<{
  type: AssetType;
  baseAsset: string;
  quoteAsset?: string;
  symbol: string;
  size?: "sm" | "md";
  className?: string;
}> = ({ type, baseAsset, quoteAsset, symbol, size = "md", className }) => {
  const icon = useSymbolIcon(type, baseAsset, quoteAsset);
  const px = size === "sm" ? 16 : 24;
  const sizeClass = size === "sm" ? "h-4 w-4" : "h-6 w-6";
  return icon ? (
    <img src={icon} alt={symbol} className={className ?? `${sizeClass} shrink-0 object-cover`} />
  ) : (
    <span
      className={`flex shrink-0 items-center justify-center bg-default-200 text-[8px] font-medium`}
      style={{ width: px, height: px }}
    >
      {baseAsset.slice(0, 2)}
    </span>
  );
};

export const PairsSelector: FC = observer(() => {
  const [isReplaceOpen, setIsReplaceOpen] = useState(false);
  const {
    allPairs,
    selectedPairs,
    favoritePairs,
    activePair,
    updateSelectedPairs,
    removePair,
    selectPairAsActive,
    replaceActivePair,
    toggleFavorite,
  } = usePairs();

  const isHorizontal = useMediaQuery("(max-height: 390px)");

  useEffect(() => {
    return accountType.subscribe((next) => {
      if (next === "real" && Number(userStore.balance?.bonusBalance ?? 0) > 0) {
        bonusStore.reset();
        bonusStore.fetchCurrent();
      }
    });
  }, []);

  const handlePairClick = (pairId: string) => {
    selectPairAsActive(pairId);
  };

  const handleRemove = (pairId: string) => {
    removePair(pairId);
  };

  const activePairs = allPairs.filter((p) => p.isTradingEnabled);

  const sharedAddPairProps = {
    activePair,
    pairs: allPairs,
    setSelectedPairs: (newPairsOrUpdater: any) => {
      if (typeof newPairsOrUpdater === "function") {
        const newPairs = newPairsOrUpdater(selectedPairs || []);
        updateSelectedPairs(newPairs);
      } else {
        updateSelectedPairs(newPairsOrUpdater);
      }
    },
    disabledPairs: selectedPairs?.map((el) => el.id) || [],
    selectPairAsActive,
    selectedPairs: selectedPairs || [],
    favoritePairs: favoritePairs || [],
    onToggleFavorite: toggleFavorite,
  };

  // Auto-select first available pair only on initial load when no pair is selected
  useEffect(() => {
    if (!activePairs.length) return;
    if (!activePair) {
      replaceActivePair(activePairs[0]!);
    }
  }, [activePairs.map((p) => p.id).join(","), activePair?.id]);

  const otherPairs =
    selectedPairs
      ?.filter((p) => p.id !== activePair?.id && p.isTradingEnabled)
      .filter((pair, index, self) => index === self.findIndex((p) => p.id === pair.id)) || [];

  return (
    <>
      {/* Mobile: only white active pair button, positioned absolute */}
      <div
        className={cn(
          "flex justify-center items-center gap-2 lg:hidden absolute z-22",
          isHorizontal ? "top-2.5 left-22" : "top-1 left-2",
        )}
      >
        <StarOutline />
        {activePair && (
          <AddPair {...sharedAddPairProps} mode="replace" onReplace={replaceActivePair} onOpenChange={setIsReplaceOpen}>
            <Button
              size="sm"
              variant="faded"
              className="shrink-0 inline-flex w-[130px] px-3 py-1 rounded-lg border-small items-center gap-1 text-black text-[12px] leading-4 border-[#D7DBDF] h-[27px] max-h-[27px] justify-between"
              style={{ background: "linear-gradient(0deg, #EAEAEA 0%, rgba(234, 234, 234, 0.6) 100%)" }}
            >
              <SymbolIcon
                type={activePair.type}
                baseAsset={activePair.baseAsset}
                quoteAsset={activePair.quoteAsset}
                symbol={activePair.symbol}
                className="h-4 w-4 shrink-0 object-cover"
              />
              <div className="flex flex-col items-start leading-none mr-1">
                <p className="text-[10px] truncate max-w-20 leading-tight">{activePair.symbol}</p>
              </div>
              <Down
                className="transition-transform duration-200"
                style={isReplaceOpen ? { transform: "rotate(180deg)" } : undefined}
              />
            </Button>
          </AddPair>
        )}
      </div>

      {/* Desktop: full selector */}
      <div className="hidden lg:flex w-full max-w-full items-center gap-2 overflow-hidden py-1.5">
        <div className="flex-shrink-0">
          <AddPair {...sharedAddPairProps} mode="add" />
        </div>

        {activePair && (
          <div className="flex-shrink-0">
            <AddPair
              {...sharedAddPairProps}
              mode="replace"
              onReplace={replaceActivePair}
              onOpenChange={setIsReplaceOpen}
            >
              <Button
                size="lg"
                variant="bordered"
                className="shrink-0 inline-flex px-4 rounded-xl border-small items-center gap-2 text-black border-white cursor-pointer"
                style={{ background: "linear-gradient(0deg, #EAEAEA 0%, rgba(234, 234, 234, 0.6) 100%)" }}
              >
                <SymbolIcon
                  type={activePair.type}
                  baseAsset={activePair.baseAsset}
                  quoteAsset={activePair.quoteAsset}
                  symbol={activePair.symbol}
                  className="h-6 w-6 shrink-0 rounded-full object-cover"
                />
                <div className="flex flex-col items-start leading-none">
                  <p className="text-[14px] truncate font-semibold max-w-28 leading-tight">{activePair.symbol}</p>
                  {tickerStore.prices.get(activePair.symbol) !== undefined && (
                    <p className="text-[12px] truncate max-w-28 leading-tight text-black">
                      {formatPriceValue(tickerStore.prices.get(activePair.symbol)!, activePair.pricePrecision)}
                    </p>
                  )}
                </div>
                <Down
                  className="transition-transform duration-200"
                  style={isReplaceOpen ? { transform: "rotate(180deg)" } : undefined}
                />
              </Button>
            </AddPair>
          </div>
        )}

        <div className="shrink-0 h-6 w-px bg-white opacity-25" />

        <div className="relative flex-1 w-0 overflow-hidden">
          <div
            className="
              scroll-x-hide
              flex gap-2 w-full
              overflow-x-auto overflow-y-hidden
              whitespace-nowrap
              touch-pan-x
              [-webkit-overflow-scrolling:touch]
            "
            onWheel={(e) => {
              const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
              if (delta !== 0) {
                e.currentTarget.scrollLeft += delta;
                e.preventDefault();
              }
            }}
          >
            {otherPairs.map((pair) => (
              <div
                key={pair.id}
                className="relative shrink-0 rounded-xl p-[0.77px]"
                style={{
                  background: "linear-gradient(180deg, rgba(245, 165, 36, 0.25) 0%, rgba(245, 165, 36, 0.025) 100%)",
                }}
              >
                <Button
                  size="lg"
                  className="relative overflow-hidden shrink-0 inline-flex px-4 rounded-xl items-center gap-2 bg-background"
                  onClick={() => handlePairClick(pair.id)}
                >
                  <span
                    className="pointer-events-none absolute inset-0 rounded-xl block"
                    style={{
                      background:
                        "radial-gradient(100% 100% at 70% 60%, rgba(245, 165, 36, 0.20) 0%, rgba(245, 165, 36, 0) 100%)",
                    }}
                  />
                  <SymbolIcon
                    type={pair.type}
                    baseAsset={pair.baseAsset}
                    quoteAsset={pair.quoteAsset}
                    symbol={pair.symbol}
                  />
                  <div className="flex flex-col items-start leading-none">
                    <p className="text-[11px] truncate max-w-28 leading-tight">{pair.symbol}</p>
                    <p className="text-[11px] truncate text-success max-w-28 leading-tight">
                      {getPairMaxPayoutPercent(pair)}%
                    </p>
                  </div>

                  <Button
                    isIconOnly
                    variant="light"
                    size="sm"
                    className="flex"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(pair.id);
                    }}
                  >
                    <Cross className="text-[#FFFFFF40]" />
                  </Button>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
});
